import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const [scrollTopVisible, setScrollTopVisible] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setScrollTopVisible(true);
      } else {
        setScrollTopVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const Section = ({ id, title, children }) => (
    <div className="section" style={styles.section}>
      <h2 
        onClick={() => toggleSection(id)} 
        className={collapsedSections[id] ? 'collapsed' : ''}
        style={styles.h2}
      >
        {title}
      </h2>
      <div 
        className={`collapsible-content ${collapsedSections[id] ? 'collapsed' : ''}`}
        style={{
          ...styles.collapsibleContent,
          ...(collapsedSections[id] ? styles.collapsibleContentCollapsed : {})
        }}
      >
        {children}
      </div>
    </div>
  );

  return (
  <>
  <Header />
    <div style={styles.body}>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(180deg); }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        .section:nth-child(1) { animation: fadeIn 0.6s ease-out 0.1s forwards; }
        .section:nth-child(2) { animation: fadeIn 0.6s ease-out 0.2s forwards; }
        .section:nth-child(3) { animation: fadeIn 0.6s ease-out 0.3s forwards; }
        .section:nth-child(4) { animation: fadeIn 0.6s ease-out 0.4s forwards; }
        .section:nth-child(5) { animation: fadeIn 0.6s ease-out 0.5s forwards; }
        .section:nth-child(6) { animation: fadeIn 0.6s ease-out 0.6s forwards; }
        .section:nth-child(7) { animation: fadeIn 0.6s ease-out 0.7s forwards; }
        .section:nth-child(8) { animation: fadeIn 0.6s ease-out 0.8s forwards; }
        .section:nth-child(9) { animation: fadeIn 0.6s ease-out 0.9s forwards; }
        .section:nth-child(10) { animation: fadeIn 0.6s ease-out 1s forwards; }
        .section:nth-child(11) { animation: fadeIn 0.6s ease-out 1.1s forwards; }

        h2::after {
          content: '▼';
          position: absolute;
          right: 0;
          font-size: 0.6em;
          transition: transform 0.3s;
        }

        h2.collapsed::after {
          transform: rotate(-90deg);
        }

        @media (max-width: 768px) {
          .content {
            padding: 30px 20px !important;
          }
          h1 {
            font-size: 2em !important;
          }
          h2 {
            font-size: 1.5em !important;
          }
        }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerBefore}></div>
          <h1 style={styles.h1}>Privacy Policy</h1>
          <p style={styles.lastUpdated}>Last Updated: January 1, 2025</p>
        </header>

        <div style={styles.content}>
          <div style={styles.intro}>
            <p style={styles.p}>
              At <strong>FigBot</strong> ("we", "us", "our"), we are committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, store, and safeguard your information when 
              you use our website, dashboard, and AI chatbot services ("Service").
            </p>
            <p style={styles.p}>
              By using FigBot, you agree to the practices described in this policy. If you do not agree, 
              please stop using the Service.
            </p>
          </div>

          <Section id="section1" title="1. Information We Collect">
            <p style={styles.p}>We collect the following types of data:</p>
            
            <h3 style={styles.h3}>1.1 Information You Provide</h3>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Account details:</strong> Name, email address, password</li>
              <li style={styles.li}><strong>Billing information:</strong> Payment method, transaction details (processed securely through third-party payment providers)</li>
              <li style={styles.li}><strong>Website details:</strong> URLs where FigBot is installed</li>
              <li style={styles.li}><strong>Content provided:</strong> Any custom instructions or settings added in the dashboard</li>
            </ul>
            <p style={{...styles.p, ...styles.highlight}}>We do not store full credit card information.</p>

            <h3 style={styles.h3}>1.2 Data Collected Automatically</h3>
            <p style={styles.p}>When you use FigBot or visit our website, we automatically collect:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>IP address</li>
              <li style={styles.li}>Browser type and version</li>
              <li style={styles.li}>Device information</li>
              <li style={styles.li}>Usage logs</li>
              <li style={styles.li}>Referrer URLs</li>
              <li style={styles.li}>Pages visited</li>
              <li style={styles.li}>Interaction analytics</li>
            </ul>
            <p style={styles.p}>This helps us improve performance, security, and user experience.</p>

            <h3 style={styles.h3}>1.3 Chatbot Data</h3>
            <p style={styles.p}>When your website visitors interact with FigBot:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>We collect chat messages</li>
              <li style={styles.li}>We log user requests</li>
              <li style={styles.li}>We store bot responses</li>
            </ul>
            <p style={styles.p}>This is used to:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Improve accuracy</li>
              <li style={styles.li}>Monitor abuse</li>
              <li style={styles.li}>Ensure service quality</li>
              <li style={styles.li}>Provide analytics to you</li>
            </ul>
            <p style={{...styles.p, ...styles.highlight}}>We do NOT use your visitor data to train public models.</p>
          </Section>

          <Section id="section2" title="2. How We Use Your Information">
            <p style={styles.p}>We use data for the following purposes:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>To operate and maintain the FigBot Service</li>
              <li style={styles.li}>To generate responses through the chatbot</li>
              <li style={styles.li}>To improve system accuracy and performance</li>
              <li style={styles.li}>To provide customer support</li>
              <li style={styles.li}>To analyze usage trends and improve features</li>
              <li style={styles.li}>To send important updates or security alerts</li>
              <li style={styles.li}>To process subscription payments</li>
              <li style={styles.li}>To prevent fraud, abuse, or misuse</li>
            </ul>
          </Section>

          <Section id="section3" title="3. How We Share Information">
            <p style={{...styles.p, ...styles.highlight}}>We do NOT sell or rent your data.</p>
            <p style={styles.p}>We may share information only with:</p>

            <h3 style={styles.h3}>3.1 Trusted Third-Party Providers</h3>
            <p style={styles.p}>Such as:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Payment processors (e.g., Stripe, PayPal)</li>
              <li style={styles.li}>Cloud hosting providers (e.g., AWS, Google Cloud)</li>
              <li style={styles.li}>AI model providers</li>
              <li style={styles.li}>Security and analytics tools</li>
            </ul>
            <p style={styles.p}>They only receive the minimum data required to perform their function.</p>

            <h3 style={styles.h3}>3.2 Legal Requirements</h3>
            <p style={styles.p}>We may disclose data if required:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>To comply with applicable laws</li>
              <li style={styles.li}>To respond to legal requests (court orders, subpoenas)</li>
              <li style={styles.li}>To protect rights, safety, or security</li>
            </ul>
          </Section>

          <Section id="section4" title="4. Data Storage & Security">
            <p style={styles.p}>We use industry-standard security measures, including:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>SSL/TLS encryption for data in transit</li>
              <li style={styles.li}>Secure cloud hosting with encrypted databases</li>
              <li style={styles.li}>Role-based access controls</li>
              <li style={styles.li}>Password hashing using bcrypt or similar algorithms</li>
              <li style={styles.li}>Regular security audits and vulnerability assessments</li>
            </ul>
            <p style={styles.p}>While we take all necessary precautions, no system can be 100% secure. We encourage users to use strong passwords and enable two-factor authentication where available.</p>
          </Section>

          <Section id="section5" title="5. Data Retention">
            <p style={styles.p}>We retain data only as long as necessary for:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Providing the Service (active accounts)</li>
              <li style={styles.li}>Legal compliance (tax records, transaction history)</li>
              <li style={styles.li}>System backup and integrity (typically 30-90 days)</li>
              <li style={styles.li}>Business reporting and analytics</li>
            </ul>
            <p style={styles.p}>Chat logs may be stored for analytics purposes unless you delete them manually through your dashboard or request removal by contacting support.</p>
            <p style={styles.p}>Upon account deletion, we will remove or anonymize your personal data within 90 days, except where retention is required by law.</p>
          </Section>

          <Section id="section6" title="6. Cookies & Tracking Technologies">
            <p style={styles.p}>We use cookies and similar technologies to:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Remember user preferences and settings</li>
              <li style={styles.li}>Keep you logged in securely</li>
              <li style={styles.li}>Improve website experience and functionality</li>
              <li style={styles.li}>Analyze traffic patterns and usage statistics</li>
            </ul>
            <p style={styles.p}>Types of cookies we use:</p>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Essential cookies:</strong> Required for basic functionality</li>
              <li style={styles.li}><strong>Performance cookies:</strong> Help us understand how visitors use our site</li>
              <li style={styles.li}><strong>Functional cookies:</strong> Remember your preferences</li>
            </ul>
            <p style={styles.p}>You can disable cookies through your browser settings, but some features may not work properly. Most browsers allow you to control cookies through their privacy settings.</p>
          </Section>

          <Section id="section7" title="7. Your Rights">
            <p style={styles.p}>Depending on your region (GDPR, CCPA, etc.), you may have the following rights:</p>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Access:</strong> Request a copy of your personal data</li>
              <li style={styles.li}><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li style={styles.li}><strong>Deletion:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li style={styles.li}><strong>Restriction:</strong> Request limited processing of your data</li>
              <li style={styles.li}><strong>Portability:</strong> Export your data in a machine-readable format</li>
              <li style={styles.li}><strong>Objection:</strong> Object to certain processing activities</li>
              <li style={styles.li}><strong>Withdraw consent:</strong> Withdraw consent for data processing</li>
            </ul>
            <p style={styles.p}>
              To exercise any of these rights, please contact us at <a href="mailto:support@figbot.ai" style={styles.link}>support@figbot.ai</a>. 
              We will respond to your request within 30 days.
            </p>
          </Section>

          <Section id="section8" title="8. Children's Privacy">
            <p style={styles.p}>
              FigBot is not intended for individuals under 18 years old (or the age of majority in your jurisdiction). 
              We do not knowingly collect personal information from minors. If we become aware that we have collected 
              data from a child without parental consent, we will take steps to delete that information promptly.
            </p>
            <p style={styles.p}>
              If you believe we have inadvertently collected information from a minor, please contact us immediately 
              at <a href="mailto:support@figbot.ai" style={styles.link}>support@figbot.ai</a>.
            </p>
          </Section>

          <Section id="section9" title="9. Third-Party Links">
            <p style={styles.p}>
              Our Service may contain links to third-party websites, services, or applications. We are not responsible for:
            </p>
            <ul style={styles.ul}>
              <li style={styles.li}>Their privacy policies or practices</li>
              <li style={styles.li}>Their security measures</li>
              <li style={styles.li}>Their content or accuracy</li>
            </ul>
            <p style={styles.p}>
              We recommend reviewing the privacy policies of any third-party sites you visit. Use third-party services at your own risk.
            </p>
          </Section>

          <Section id="section10" title="10. International Data Transfers">
            <p style={styles.p}>
              Your data may be processed and stored on servers located in different countries, including the United States 
              and European Union. We ensure appropriate safeguards are in place when transferring data internationally, 
              including Standard Contractual Clauses (SCCs) approved by the European Commission.
            </p>
            <p style={styles.p}>
              By using FigBot, you consent to the transfer of your information to countries outside your country of residence, 
              which may have different data protection laws.
            </p>
          </Section>

          <Section id="section11" title="11. Changes to This Policy">
            <p style={styles.p}>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
              legal requirements, or other factors. We will notify users of significant changes via:
            </p>
            <ul style={styles.ul}>
              <li style={styles.li}>Email notification to registered users</li>
              <li style={styles.li}>Dashboard notification when you log in</li>
              <li style={styles.li}>Updated "Last Updated" date at the top of this page</li>
            </ul>
            <p style={styles.p}>
              Continued use of the Service after changes are posted indicates your acceptance of the updated policy. 
              We encourage you to review this policy periodically.
            </p>
          </Section>

          <div style={styles.contactBox}>
            <h2 style={styles.contactBoxH2}>12. Contact Us</h2>
            <p style={styles.contactBoxP}>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <p style={styles.contactBoxP}>
              <strong>Email:</strong> <a href="mailto:support@figbot.ai" style={styles.contactBoxLink}>support@figbot.ai</a>
            </p>
            <p style={styles.contactBoxP}>
              <strong>Website:</strong> <a href="https://www.figbot.ai" style={styles.contactBoxLink} target="_blank" rel="noopener noreferrer">www.figbot.ai</a>
            </p>
            <p style={styles.contactBoxP}>
              <strong>Mailing Address:</strong> FigBot Inc., [Insert Company Address]
            </p>
            <p style={styles.contactBoxP}>
              <strong>Data Protection Officer:</strong> privacy@figbot.ai
            </p>
          </div>
        </div>

        <footer style={styles.footer}>
          <p>&copy; 2025 FigBot. All rights reserved.</p>
        </footer>
      </div>

      <button 
        style={{
          ...styles.scrollTop,
          opacity: scrollTopVisible ? 1 : 0,
          pointerEvents: scrollTopVisible ? 'auto' : 'none'
        }}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        ↑
      </button>
    </div>
    <Footer/>
  </>
  );
};

const styles = {
  body: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    lineHeight: 1.6,
    color: '#333',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '100vh',
    padding: '20px',
    margin: 0,
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    animation: 'fadeInUp 0.6s ease-out',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '60px 40px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  headerBefore: {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    animation: 'pulse 15s ease-in-out infinite',
  },
  h1: {
    fontSize: '2.5em',
    fontWeight: 700,
    marginBottom: '10px',
    position: 'relative',
    zIndex: 1,
  },
  lastUpdated: {
    fontSize: '0.9em',
    opacity: 0.9,
    position: 'relative',
    zIndex: 1,
  },
  content: {
    padding: '50px 40px',
  },
  intro: {
    background: '#f8f9fa',
    borderLeft: '4px solid #667eea',
    padding: '20px',
    marginBottom: '30px',
    borderRadius: '8px',
    animation: 'slideIn 0.6s ease-out 0.2s both',
  },
  section: {
    marginBottom: '40px',
    opacity: 0,
  },
  h2: {
    color: '#667eea',
    fontSize: '1.8em',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '2px solid #e9ecef',
    cursor: 'pointer',
    transition: 'color 0.3s',
    position: 'relative',
  },
  h3: {
    color: '#495057',
    fontSize: '1.3em',
    margin: '20px 0 10px',
  },
  p: {
    marginBottom: '15px',
    color: '#555',
  },
  ul: {
    marginLeft: '30px',
    marginBottom: '15px',
  },
  li: {
    marginBottom: '8px',
    color: '#555',
  },
  collapsibleContent: {
    maxHeight: '5000px',
    overflow: 'hidden',
    transition: 'max-height 0.4s ease-out, opacity 0.3s',
    opacity: 1,
  },
  collapsibleContentCollapsed: {
    maxHeight: '0',
    opacity: 0,
  },
  contactBox: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '12px',
    marginTop: '40px',
    textAlign: 'center',
  },
  contactBoxH2: {
    color: 'white',
    border: 'none',
    cursor: 'default',
    fontSize: '1.8em',
    marginBottom: '15px',
  },
  contactBoxP: {
    marginBottom: '15px',
    color: 'white',
  },
  contactBoxLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: 600,
    borderBottom: '2px solid rgba(255,255,255,0.5)',
    transition: 'border-color 0.3s',
  },
  highlight: {
    background: '#fff3cd',
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: 600,
    borderBottom: '2px solid rgba(102, 126, 234, 0.3)',
    transition: 'border-color 0.3s',
  },
  footer: {
    background: '#f8f9fa',
    padding: '20px 40px',
    textAlign: 'center',
    color: '#6c757d',
    fontSize: '0.9em',
  },
  scrollTop: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    background: '#667eea',
    color: 'white',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.5em',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    transition: 'opacity 0.3s, transform 0.3s, background 0.3s',
    zIndex: 1000,
  },
};

export default PrivacyPolicy;

